/* eslint-disable max-len */
import React, { useState, useCallback } from 'react';
import classnames from 'classnames';
import injectSheet from 'react-jss';
import _ from 'underscore';
import { ErrorMessages } from '@bingads-webui-react/validation';
import { Button, Glyphicon } from '@bingads-webui-react/primitive';
import { FilePickerView } from './views/file-picker-view';
import {
  getFileValidationStatus, isListValid, generateFile, isRequiredValid,
} from './helper';
import { FilePickerStyles } from './css/styles';
import { useThrowOut } from './use-throw-out';
import { EMPTY_FILEDATA_ID, i18nKeys } from './constants';
import { FilePickerContextProvider, useFilePickerContext } from './context';
import {
  IFilePickerWithoutStylesBaseProps,
  FilePickerWithoutStylesProps,
} from './types';

function FilePickerWithoutStylesBase({
  classes,
  className,
  fileList,
  onFileChange,
  alwaysShowError,
  allowedFileExtensions,
  required,
  maxCount,
  validator,
  shouldAutoRename,
  passiveErrorMessages,
  customPlaceholder,
  exploreBtnStyle,
  ariaLabel,
}: IFilePickerWithoutStylesBaseProps) {
  const { getString } = useFilePickerContext();
  const fileValidation = useCallback((file: File) => getFileValidationStatus({
    file,
    allowedFileExtensions,
    validateFileExtensionsFailedMessage: getString(i18nKeys.FilePicker_ErrorMessage_ExtensionNotAllowed),
    customValidator: validator,
  }), [allowedFileExtensions, getString, validator]);

  const {
    validatedFileList,
    edited,
    displayEmptyFile,
    setEdited,
    setDisplayEmptyFile,
    setFileList,
  } = useThrowOut({
    onFileChange,
    required,
    fileList,
    fileValidation,
  });

  const [actionMessage, setActionMessage] = useState<string | null>(null);

  const handleAddNewFile = ({ file, isValid, errorMessages }: { file: File; isValid: boolean; errorMessages: string[] }) => {
    setDisplayEmptyFile(false);
    const newFileList = [...validatedFileList, generateFile({
      file,
      isValid,
      shouldAutoRename,
      errorMessages,
      fileList: validatedFileList,
    })];
    setFileList(newFileList);
    setEdited(true);
    setActionMessage(getString(i18nKeys.FilePicker_ActionMessage_FileAdded, { fileName: file.name }));
  };

  const handleChangeExistFile = ({
    id, file, isValid, errorMessages,
  }: { id: string; file: File; isValid: boolean; errorMessages: string[] }) => {
    const index = _.findIndex(validatedFileList, { id });
    const newFileList = _.without(validatedFileList, validatedFileList[index]);
    const newFile = generateFile({
      file,
      isValid,
      shouldAutoRename,
      errorMessages,
      fileList: newFileList,
    });
    newFileList.splice(index, 0, newFile);
    setFileList(newFileList);
    setEdited(true);
    setActionMessage(getString(i18nKeys.FilePicker_ActionMessage_FileChanged));
  };

  const handleDeleteFile = (fileId: string) => {
    const newFileList = _.filter(validatedFileList, ({ id }) => fileId !== id);
    setFileList(newFileList);
    if (newFileList.length === 0) {
      setDisplayEmptyFile(true);
    }
    setEdited(true);
    setActionMessage(getString(i18nKeys.FilePicker_ActionMessage_FileRemoved));
  };

  const renderFileList = () => _.map(validatedFileList, fileData => (
    <FilePickerView
      key={fileData.id}
      className="picked-row"
      data={fileData}
      onDelete={handleDeleteFile}
      onFileChange={handleChangeExistFile}
      allowedFileExtensions={allowedFileExtensions}
      validator={fileValidation}
      customPlaceholder={customPlaceholder}
      exploreBtnStyle={exploreBtnStyle}
      ariaLabel={ariaLabel}
    />
  ));

  const renderEmptyFilePicker = () => displayEmptyFile && (
    <FilePickerView
      key={EMPTY_FILEDATA_ID}
      className="empty-row"
      data={generateFile({ id: EMPTY_FILEDATA_ID, isValid: true })}
      onFileChange={handleAddNewFile}
      allowedFileExtensions={allowedFileExtensions}
      validator={fileValidation}
      customPlaceholder={customPlaceholder}
      exploreBtnStyle={exploreBtnStyle}
      required={required}
      ariaLabel={ariaLabel}
    />
  );

  const canRenderAddNewButton = () => {
    const emptyFileCount = displayEmptyFile ? 1 : 0;
    const validatedFileListCount = _.result(validatedFileList, 'length') || 0;

    return maxCount > 1 && (emptyFileCount + validatedFileListCount < maxCount);
  };

  const renderAddNewButton = () => canRenderAddNewButton() && (
    <Button
      disabled={displayEmptyFile || !isListValid(validatedFileList)}
      onClick={() => setDisplayEmptyFile(true)}
      className="file-picker-add-new"
    >
      <Glyphicon className="file-picker-add-new-icon" glyph="ba-Add" />
      <span className="file-picker-add-new-text">{getString(i18nKeys.FilePicker_AddNewFile)}</span>
    </Button>
  );

  const renderErrorMessage = () => {
    const internalErrorMessages = (edited || alwaysShowError)
      ? _.compact([
        !isRequiredValid({ list: validatedFileList, required }) && getString(i18nKeys.FilePicker_ErrorMessage_Required),
      ])
      : [];

    const errorMessages = [...internalErrorMessages, ...passiveErrorMessages];

    return !_.isEmpty(errorMessages) && (
      <div className="file-picker-container-error">
        <ErrorMessages messages={errorMessages} />
      </div>
    );
  };

  // This is used to render a hidden aria message to allow the screen reader to announce state change.
  const renderHiddenAriaActionMessage = () => !_.isNull(actionMessage) && (
    <div className="file-picker-action-message" role="alert" aria-live="polite">
      {actionMessage}
    </div>
  );

  return (
    <div className={classnames('file-picker-container', classes.filePicker, className)}>
      {renderHiddenAriaActionMessage()}
      {renderFileList()}
      {renderEmptyFilePicker()}
      {renderAddNewButton()}
      {renderErrorMessage()}
    </div>
  );
}

FilePickerWithoutStylesBase.defaultProps = {
  className: '',
  fileList: [],
  maxCount: 10,
  validator: _.noop,
  allowedFileExtensions: [],
  required: false,
  alwaysShowError: false,
  shouldAutoRename: false,
  passiveErrorMessages: [],
  customPlaceholder: '',
  exploreBtnStyle: 'primary',
  ariaLabel: '',
};

const FilePickerWithoutStyles = (props: FilePickerWithoutStylesProps) => (
  <FilePickerContextProvider i18n={props.i18n} customStrings={props.customStrings}>
    <FilePickerWithoutStylesBase {...props} />
  </FilePickerContextProvider>
);

FilePickerWithoutStyles.defaultProps = {
  customStrings: {},
};

export const FilePicker = injectSheet(FilePickerStyles)(FilePickerWithoutStyles);
